// Stale-deploy / chunk-load recovery.
//
// When a new build is deployed, every lazily-imported JS/CSS chunk gets a
// new content-hash in its filename. A browser tab that loaded the OLD
// index.html still remembers the OLD hashes; the next time it lazy-loads a
// route chunk it requests a filename the server no longer has → the import
// rejects with "Failed to fetch dynamically imported module" and the app
// falls into the error boundary.
//
// The correct recovery for THIS class of error (unlike a render exception)
// is a single hard reload: it refetches index.html, which points at the new
// hashes. We guard with a short-lived sessionStorage stamp so that a chunk
// that is genuinely missing (not merely stale) can't trap the user in an
// infinite refresh loop — after one attempt we let the error surface.

/** True if the error looks like a stale-deploy dynamic-import/chunk failure. */
export function isChunkLoadError(err: unknown): boolean {
  const msg =
    err && typeof err === "object" && "message" in err
      ? String((err as { message?: unknown }).message ?? "")
      : String(err ?? "");
  return /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|dynamically imported module|ChunkLoadError|Loading chunk [\w-]+ failed|Loading CSS chunk/i.test(
    msg,
  );
}

const RELOAD_STAMP_KEY = "coe-chunk-reload-at";
const RELOAD_GUARD_MS = 20000;

/**
 * Reload once to recover from a stale chunk, guarding against loops.
 * Returns true if a reload was triggered (caller should stop work — the
 * page is about to navigate away).
 */
export function attemptChunkReloadOnce(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_STAMP_KEY) || "0");
    const now = Date.now();
    // Already reloaded recently for this reason → don't loop; let it surface.
    if (now - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_STAMP_KEY, String(now));
    // Cache-bust the document so the browser refetches index.html rather
    // than serving a stale copy that still references the dead chunks.
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}
